import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { bool, object, string } from 'yup';

import { Button } from '@/src/shared/presentation/components/button';
import { ControlledCheckbox } from '@/src/shared/presentation/components/checkbox';
import { ControlledInput } from '@/src/shared/presentation/components/input';
import { ControlledRecaptchaInput } from '@/src/shared/presentation/components/recaptcha-input/recaptcha-input';
import { withProviders } from '@/src/shared/presentation/utils/with-providers';

import SubscribeToNewsletterProvider, {
  useSubscribeToNewsletterProvider
} from '../providers/subscribe-to-newsletter-provider';

import css from './subscribe-to-newsletter-form.css';

import type { SubscribeToNewsletterFormData } from '../view-models/subscribe-to-newsletter-form-data';
import type { ObjectSchema } from 'yup';

function SubscribeToNewsletterForm() {
  const { isLoading, subscribeToNewsletter } = useSubscribeToNewsletterProvider((state) => state);
  const { t } = useTranslation('newsletter', {
    keyPrefix: 'components.subscribe_to_newsletter_form'
  });
  const defaultValues: SubscribeToNewsletterFormData = {
    email: '',
    consent: false,
    recaptcha: false
  };
  const validationSchema: ObjectSchema<SubscribeToNewsletterFormData> = useMemo(
    () =>
      object().shape({
        email: string().required().email(),
        consent: bool().required().isTrue(),
        recaptcha: bool().required().isTrue()
      }),
    []
  );
  const form = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema, { abortEarly: false }),
    reValidateMode: 'onChange'
  });
  const onSubmit = form.handleSubmit(async (data: SubscribeToNewsletterFormData) => {
    await subscribeToNewsletter(data);
  });

  return (
    <FormProvider {...form}>
      <form className={css.form} onSubmit={onSubmit}>
        <div className={css.formRow}>
          <ControlledInput name="email" label={`${t('fields.email.label')}`} />
          <Button type="submit" disabled={!form.formState.isValid || isLoading}>
            {t('submit_button')}
          </Button>
        </div>
        <ControlledCheckbox name="consent" label={t('fields.consent.label')} />
        <ControlledRecaptchaInput name="recaptcha" />
      </form>
    </FormProvider>
  );
}

export default withProviders(SubscribeToNewsletterProvider)(SubscribeToNewsletterForm);
